/**
 * Fast-path SSO when Sign In is opened with ?username=email.
 * Uses HeadEnd-prefetched /identity/externalproviders when available, then runs
 * the normal SignInExternalProvider chain (login_hint + returnUrl).
 *
 * If the user is already signed in (non-guest), skips SSO and sends them to
 * ?returnUrl= or home instead.
 */
import {
    clearAutoSsoPendingUi,
    resolveSignedInSignInLandingUrl,
    shouldAutoSignInExternalSso,
} from "@iewcasia/Utilities/autoSignInExternalSso";
import {
    createHandlerChainRunnerOptionalParameter,
    HandlerWithResult,
    HasOnComplete,
} from "@insite/client-framework/HandlerCreator";
import {
    ExternalProviderLinkCollectionModel,
    getExternalProviders,
} from "@insite/client-framework/Services/IdentityService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signInExternalProvider from "@insite/client-framework/Store/Context/Handlers/SignInExternalProvider";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";

type Parameter = HasOnComplete;
type HandlerType = HandlerWithResult<Parameter, ExternalProviderLinkCollectionModel | undefined>;

export const GuardAutoSso: HandlerType = props => {
    const state = props.getState();
    const { session } = state.context;
    const location = getLocation(state);
    const enableStorefrontSso = !!(getSettingsCollection(state) as any).azureB2CGraphSettings?.enableStorefrontSso;
    const { shouldAutoSso } = shouldAutoSignInExternalSso(location.search, enableStorefrontSso);

    // Already signed in: never start SSO; leave Sign In for returnUrl/home.
    if (session.isAuthenticated && !session.isGuest) {
        const landingUrl = resolveSignedInSignInLandingUrl(location.search, getHomePageUrl(state));
        // Keep overlay until navigation (SignInPage treats result.url as "redirect started").
        props.result = {
            externalProviders: [{ caption: "AlreadySignedIn", url: landingUrl }],
        };
        window.location.replace(landingUrl);
        return false;
    }

    if (!shouldAutoSso) {
        clearAutoSsoPendingUi();
        return false;
    }
};

export const RequestExternalProviders: HandlerType = async props => {
    const prefetched =
        typeof window !== "undefined" ? window.__iewcasiaAutoSsoProvidersPromise : undefined;

    if (prefetched) {
        const prefetchedResult = await prefetched;
        if (prefetchedResult?.externalProviders?.length) {
            props.result = prefetchedResult;
            return;
        }
    }

    props.result = await getExternalProviders();
};

export const StartExternalProviderSignIn: HandlerType = props => {
    const externalProvider = props.result?.externalProviders?.[0];
    if (!externalProvider?.url) {
        clearAutoSsoPendingUi();
        return false;
    }

    props.dispatch(signInExternalProvider({ externalProvider }));
};

export const chain = [GuardAutoSso, RequestExternalProviders, StartExternalProviderSignIn];

const autoSignInExternalFromUsername = createHandlerChainRunnerOptionalParameter(
    chain,
    {},
    "AutoSignInExternalFromUsername",
);

export default autoSignInExternalFromUsername;
